import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Button,
  Box,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Lock as LockIcon,
  Clear as ClearIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { AttributeLockState } from '../types/index';

interface AttributeLockProps {
  attributeLock: AttributeLockState;
  onLockChange: (attribute: 'people' | 'location' | 'time' | null, value: string | null) => void;
  uniquePeople: Array<{ originalName: string; displayName: string }>;
  uniqueMapLocations: string[];
  uniqueLocalLocations: string[];
  uniqueDates: string[];
  allTimes: string[];
  startTimeRange: number;
  endTimeRange: number;
  onTimeRangeChange: (start: number, end: number) => void;
  getTimestampFromSlider: (value: number) => number;
  getSliderValueFromTimestamp: (timestamp: number) => number;
}

const AttributeLock: React.FC<AttributeLockProps> = ({
  attributeLock,
  onLockChange,
  uniquePeople,
  uniqueMapLocations,
  uniqueLocalLocations,
  uniqueDates,
  allTimes,
  startTimeRange,
  endTimeRange,
  onTimeRangeChange,
  getTimestampFromSlider,
  getSliderValueFromTimestamp
}) => {
  // No automatic lock logic in useEffect - only trigger locks on user interaction

  return (
    <Box sx={{ mb: 1 }}>
      <Accordion defaultExpanded sx={{ boxShadow: 'none', border: 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ 
          minHeight: 40, 
          backgroundColor: '#f8f9fa',
          borderBottom: 'none'
        }}>
          <Box display="flex" alignItems="center">
            <LockIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ 
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#2c3e50'
            }}>
              Lock Attribute
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1, pb: 1 }}>

                 {/* People and Location Lock - Same Row */}
         <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
           <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
             <InputLabel>
               <Box display="flex" alignItems="center">
                 <PersonIcon sx={{ mr: 0.5, fontSize: 16 }} />
                 People
               </Box>
             </InputLabel>
            <Select
              value={attributeLock.attribute === 'people' ? attributeLock.value || '' : ''}
              onChange={(e) => onLockChange('people', e.target.value || null)}
                           label={
                 <Box display="flex" alignItems="center">
                   <PersonIcon sx={{ mr: 0.5, fontSize: 16 }} />
                   People
                 </Box>
               }
            >
              <MenuItem value="">Select a person</MenuItem>
              {uniquePeople.map(person => (
                <MenuItem key={person.originalName} value={person.originalName}>
                  {person.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

           <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
             <InputLabel>
               <Box display="flex" alignItems="center">
                 <LocationIcon sx={{ mr: 0.5, fontSize: 16 }} />
                 Location
               </Box>
             </InputLabel>
            <Select
              value={attributeLock.attribute === 'location' ? attributeLock.value || '' : ''}
              onChange={(e) => onLockChange('location', e.target.value || null)}
                           label={
                 <Box display="flex" alignItems="center">
                   <LocationIcon sx={{ mr: 0.5, fontSize: 16 }} />
                   Location
                 </Box>
               }
            >
              <MenuItem value="">Select a location</MenuItem>
              {uniqueMapLocations.map(location => (
                <MenuItem key={location} value={location}>{location}</MenuItem>
              ))}
              {uniqueLocalLocations.map(location => (
                <MenuItem key={location} value={location}>{location}</MenuItem>
              ))}
            </Select>
          </FormControl>
         </Box>

                          {/* Time Range Lock */}
         <Box sx={{ p: 1.5, mb: 1.5 }}>
           <Box display="flex" alignItems="flex-start" sx={{ mb: 1 }}>
             <ScheduleIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18, mt: 0.5 }} />
             <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', mr: 2 }}>Time Range</Typography>
             <Box sx={{ flex: 1, px: 1 }}>
            <Slider
              value={[startTimeRange, endTimeRange]}
              min={0}
              max={100}
              step={1}
              onChange={(_, newValue) => {
                const [start, end] = newValue as number[];
                onTimeRangeChange(start, end);
              }}
              onChangeCommitted={(_, newValue) => {
                const [start, end] = newValue as number[];
                // Trigger lock logic when slider movement is completed
                const startTime = getTimestampFromSlider(start);
                const endTime = getTimestampFromSlider(end);
                const startDateTime = new Date(startTime).toISOString();
                const endDateTime = new Date(endTime).toISOString();
                
                if (startDateTime === endDateTime) {
                  onLockChange('time', startDateTime);
                } else {
                  // Clear time lock if it's a range
                  onLockChange('time', null);
                }
              }}
              valueLabelDisplay="off"
              sx={{ mb: 0.5 }}
            />
            
            {/* Date/Time Labels positioned below slider */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 1 }}>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {new Date(getTimestampFromSlider(startTimeRange)).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {new Date(getTimestampFromSlider(startTimeRange)).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1, textAlign: 'right' }}>
                <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {new Date(getTimestampFromSlider(endTimeRange)).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {new Date(getTimestampFromSlider(endTimeRange)).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </Typography>
              </Box>
            </Box>
                        </Box>
          </Box>
          </Box>
                 {/* Current Lock Status */}
         {attributeLock.attribute && attributeLock.value && (
           <Box mt={0.5} p={0.5} bgcolor="primary.light" borderRadius={1}>
             <Typography variant="caption" color="white" display="block">
               Currently locked: {attributeLock.attribute} = {attributeLock.value}
             </Typography>
           </Box>
         )}

         {/* Clear All Locks */}
         <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
           <Button
             variant="outlined"
             color="error"
             size="small"
             onClick={(e) => {
               e.stopPropagation();
               onLockChange(null, null);
             }}
             startIcon={<ClearIcon />}
             sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
           >
             Clear All Locks
           </Button>
         </Box>
          </AccordionDetails>
        </Accordion>
    </Box>
  );
};

export default AttributeLock;

